import * as Sentry from '@sentry/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from '@/components/feedback/snackbar';
import { odooApiService } from '@/services/odoo.service';
import type { OdooResourceType, ManualLinkResult } from '@/types/odoo.types';

interface UseOdooUnlinkOptions {
  /** The Odoo entity type. */
  resourceType: OdooResourceType;
  /** Called after a successful unlink operation. */
  onSuccess?: (result: ManualLinkResult) => void;
}

/**
 * Hook that provides a mutation to unlink a Hub resource from Odoo.
 */
export function useOdooUnlink({ resourceType, onSuccess }: UseOdooUnlinkOptions) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const { mutate, mutateAsync, isLoading, error } = useMutation({
    mutationFn: (resourceId: string) => odooApiService.unlink(resourceType, resourceId),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['odoo-search', resourceType] });
      queryClient.invalidateQueries({ queryKey: ['odoo-unlinked', resourceType] });
      enqueueSnackbar(t('Resource unlinked from Odoo successfully'), { variant: 'success' });
      onSuccess?.(result);
    },
    onError: (err: unknown) => {
      const error = err as { message?: string };
      const message = error?.message || t('An error occurred while unlinking from Odoo');
      enqueueSnackbar(message, { variant: 'error' });
      Sentry.captureException(err, {
        tags: { hook: 'useOdooUnlink', action: 'unlink', resourceType },
      });
    },
  });

  return {
    /** Trigger the unlink mutation (fire-and-forget). */
    unlink: mutate,
    /** Trigger the unlink mutation (returns a promise). */
    unlinkAsync: mutateAsync,
    /** True while the mutation is in progress. */
    isLoading,
    /** Error from the mutation, if any. */
    error,
  };
}
